// Printer Setup, Program Settings › Printer Setup. COHESION_CHANGE_LOG.md list 2 item 1 (approved 2026-10-24,
// 9:38 PM; mockup MOCKUP_ONE_PRINT_REV5.html, 2026-10-25). Robert: 99% of the time he uses the defaults, so
// the offset left the print card. The card shows one amber line while a roll is off start.
//
// The offset is kept on this iPad, not the program (printer-offset.ts). The page says so at the top.

import { handleOffsetClick, handleOffsetInput, offsetBox, printTestLabel } from "./print-card";
import { offsetWords, readOffset, ROLL_WORDS, START_OFFSET, type PrintRoll } from "./printer-offset";
import { esc, ident } from "./training-screens";

const ROLLS: readonly { roll: PrintRoll; name: string; stock: string; test: [number, number] }[] = [
  { roll: "qrRoll", name: "QR Labels And QEW Tags", stock: "one roll, 2.25 × 1.0 in on GP 1 x 3", test: [2.25, 1] },
  { roll: "fieldRoll", name: "Field Labels", stock: "yellow rolls, every size", test: [2, 1] },
  { roll: "arcFlashRoll", name: "Arc Flash Labels", stock: "WARNING and DANGER rolls, 5 × 3 in", test: [5, 3] },
];

const atStart = (roll: PrintRoll) => {
  const o = readOffset(roll);
  const s = START_OFFSET[roll];
  return o.acrossMm === s.acrossMm && o.downMm === s.downMm;
};

/** The Program Settings row's line: "This iPad · all at start", "This iPad · field labels off start". */
export function printerMenuLine(): string {
  const off = ROLLS.filter((r) => !atStart(r.roll)).map((r) => ROLL_WORDS[r.roll]);
  return `This iPad · ${off.length ? `${off.join(", ")} off start` : "all at start"}`;
}

export function printerSetupScreen(): string {
  const rolls = ROLLS.map((r) => {
    const o = readOffset(r.roll);
    const st = START_OFFSET[r.roll];
    const now = atStart(r.roll) ? `at start${st.acrossMm || st.downMm ? ` (${offsetWords(st)})` : ""}` : `${offsetWords(o)} · off start`;
    return `<div class="box ps-roll"><div class="ps-rollhd"><b>${esc(r.name)}</b><span class="${atStart(r.roll) ? "" : "off"}">${esc(r.stock)} · ${esc(now)}</span></div>${offsetBox(r.roll, o)}</div>`;
  }).join("");
  return `<a class="back" href="#/settings">Program Settings</a>
    ${ident("PT", "Printer Setup", "The Toro Max on this iPad")}
    <span class="ps-ipad">THIS IPAD</span>
    <div class="note">These offsets live on this iPad only. Another iPad keeps its own. Leave them at start unless a print comes out off the label. Then nudge, Print Test Label, and check the border against the stock edge.</div>
    ${rolls}`;
}

const rollOf = (el: HTMLElement): PrintRoll | null => {
  const r = el.dataset.roll;
  return r === "qrRoll" || r === "fieldRoll" || r === "arcFlashRoll" ? r : null;
};

/** A tap on the page. True when it was one of ours. */
export function onPrinterSetupClick(target: HTMLElement, repaint: () => void): boolean {
  const el = target.closest<HTMLElement>("[data-pc]");
  const roll = el ? rollOf(el) : null;
  if (!el || !roll || (el as HTMLButtonElement).disabled) return false;
  if (el.dataset.pc === "test") {
    const r = ROLLS.find((x) => x.roll === roll)!;
    void printTestLabel(roll, r.test[0], r.test[1]);
    return true;
  }
  if (handleOffsetClick(el, roll)) repaint();
  return true;
}

/** A typed offset. True when it was one of ours. */
export function onPrinterSetupChange(t: HTMLInputElement, repaint: () => void): boolean {
  const roll = t.dataset.pcMm ? rollOf(t) : null;
  if (!roll) return false;
  handleOffsetInput(t, roll);
  repaint();
  return true;
}
